import {useTolgee} from 'tolgee';

import {useQualityAgentAccessQuery} from '~api/auth.queries';
import {selectCurrentProjectId, selectCustomer} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {EmbeddedApp} from '../../embed/EmbeddedApp';
import {EmbedPanelLayout} from '../../embed/protocol';

// Hosts the standalone Quality Agent app in an iframe, handing it the current
// customer/project and a short-lived auth token over the embed protocol. The
// agent is a separately-deployed app; only its context + auth are shared.
//
// Self-gates on deployment + the per-user allowlist so it's safe wherever it's
// mounted — the Dashboard tab and the standalone /quality-agent route both go
// through here, and a non-allowlisted user reaching the route directly gets
// nothing rather than an iframe whose token fetch would just 403.
interface QualityAgentProps {
  panelLayout?: EmbedPanelLayout;
}

export function useIsQualityAgentVisible(): boolean {
  const isDeployed = Boolean(import.meta.env.VITE_QUALITY_AGENT_URL);
  const access = useQualityAgentAccessQuery(isDeployed);

  return isDeployed && access.isSuccess;
}

export function QualityAgent({panelLayout}: QualityAgentProps): JSX.Element | null {
  const customer = useAppSelector(selectCustomer);
  const projectId = useAppSelector(selectCurrentProjectId);
  // Subscribed to language changes, so a switch in the user settings re-renders
  // us with the new tag and EmbeddedApp pushes it to the agent live.
  const tolgee = useTolgee(['language']);
  const isVisible = useIsQualityAgentVisible();

  if (!isVisible) return null;

  return (
    <div style={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column'}}>
      <EmbeddedApp
        appId='quality-agent'
        context={{customer: customer ?? '', projectId, language: tolgee.getLanguage(), panelLayout}}
        capabilities={panelLayout ? [] : undefined}
      />
    </div>
  );
}
